/**
 * PWA bootstrapping — registers the service worker and captures the
 * `beforeinstallprompt` event so the app can offer an install prompt on
 * demand (via the InstallBanner component or the /download page CTA).
 *
 * Register from the app's entry point (index.js) via `registerPwa()`.
 */

let deferredPrompt = null;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try { fn(deferredPrompt); } catch (e) {
      // A subscriber threw — that's the subscriber's bug, not ours. Log
      // once for visibility but keep iterating so a bad listener can't
      // stop the rest of the app from receiving install-availability
      // updates. We intentionally do NOT unsubscribe automatically.
      // eslint-disable-next-line no-console
      console.error("[pwa.onInstallAvailable] subscriber threw:", e);
    }
  });
}

// Public API — subscribe to install-availability changes.
export function onInstallAvailable(fn) {
  listeners.add(fn);
  fn(deferredPrompt);
  return () => listeners.delete(fn);
}

// Programmatically fire the browser's native install dialog. Returns the
// user's choice ("accepted" | "dismissed") or null if unavailable.
export async function promptInstall() {
  if (!deferredPrompt) return null;
  const p = deferredPrompt;
  deferredPrompt = null;
  emit();
  try {
    await p.prompt();
    const result = await p.userChoice;
    return (result && result.outcome) || null;
  } catch (e) {
    // `prompt()` throws in a handful of well-known cases: the event was
    // already consumed, the user gestured too late, or the browser now
    // disallows the prompt. None of these are actionable — return null and
    // let the caller show a manual install-instructions fallback.
    // eslint-disable-next-line no-console
    console.debug("[pwa.promptInstall] install prompt unavailable:", e);
    return null;
  }
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari — non-standard flag but still in the wild.
  return !!window.navigator.standalone;
}

export function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIos && isSafari;
}

export function registerPwa() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    emit();
  });
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js", { scope: "/" })
        // eslint-disable-next-line no-console
        .catch((err) => console.warn("SW registration failed", err));
    });
  }
}
